/**
 * Phrasings that end a task or handoff at any step, never taken as a value for the
 * step asked about: "cancel", "stop", "never mind", "forget it", and "that's not
 * what I meant" (which also asks what else it might have been).
 */

const CANCEL_WORD =
  "(?:cancel|never\\s*mind|nvm|stop|forget(?:\\s+(?:it|that|this|the\\s+duplicate))?|send anything)(?:\\s+(?:it|that|this|(?:the|this)\\s+(?:notice|event|poll|task)))?";
/** "wait, never mind, cancel that": one cancel word, or several in a row */
const CANCEL = new RegExp(
  `^(?:(?:ok(?:ay)?|oh|no|wait|hold on|actually|sorry|oops|ugh|please|don'?t|do not)[,\\s]+)*${CANCEL_WORD}(?:[,.!\\s]+${CANCEL_WORD})*(?:[,\\s]+please)?[\\s.!?]*$`,
  "i"
);

/** "no, that's not what I meant", "wait no, that's not what I meant" */
const NOT_WHAT_I_MEANT =
  /^(?:(?:wait|hold on|ok(?:ay)?|no|actually|stop)[,.!\s]+)*(?:that'?s|that\s+is)\s+not\s+what\s+i\s+meant[\s.!?]*$/i;

/** "I don't know what you're talking about": the step they were given isn't one they meant to answer. */
const LOST =
  /^(?:i )?(?:don'?t|dont|do not) know what you(?:'re| are) talking about[\s.!]*$|^what are you talking about[\s.!]*$/i;

/** "no don't send it", "never mind, I'll do it tomorrow": not now, so the draft stops and nothing is sent. */
const NOT_NOW =
  /^(?:(?:no|nope|wait|stop|hold on|actually|sorry|oh)[,.!\s]+)*(?:(?:don'?t|do not)\s+(?:send|publish|post)\s+(?:it|that|this|anything)(?:\s+(?:yet|now|today))?|(?:(?:never\s*mind|nvm|forget\s+it|leave\s+it)[,.!\s]+)?i'?ll\s+(?:do|send|finish|sort)\s+(?:it|this|that)(?:\s+myself)?\s+(?:later|tomorrow|another\s+time|next\s+week|in\s+the\s+morning))(?:[,.!\s]+(?:thanks|thank\s+you|please))?[\s.!]*$/i;

/**
 * "scrap that", "abort", "don't bother", "nah leave it", "delete this draft", "ditch it": the other ways people stop.
 * Only the whole message, so "Scrapbooking club" or "leave it at the gate" is still an answer.
 */
const STOP_IT =
  /^(?:(?:no|nah|nope|ok(?:ay)?|oh|wait|actually|sorry|ugh|hmm+|never\s*mind|nvm)[,.!\s]+)*(?:(?:scrap|scratch)\s+(?:it|that|this|the\s+whole\s+thing|everything)|abort(?:\s+(?:it|that|this))?|(?:don'?t|do\s+not)\s+bother(?:[,\s]+cancel(?:\s+(?:it|that|this))?)?|leave\s+it|(?:ditch|bin|drop|kill|trash|dump)\s+(?:it|that|this)|forget\s+about\s+it|throw\s+(?:it|that|this)\s+away|i\s+(?:don'?t|do\s+not)\s+(?:want|need)\s+(?:it|this|that)(?:\s+any\s*more)?)(?:[,.!\s]+(?:please|thanks|then|for\s+now))*[\s.!]*$/i;

/** "delete this draft", "never mind, delete the notice": a cancel only while a draft is open, else a question about a sent one. */
export const DELETE_THE_DRAFT =
  /^(?:(?:no|nah|ok(?:ay)?|oh|actually|sorry|never\s*mind|nvm)[,.!\s]+)*(?:delete|remove|discard|trash)\s+(?:this|the|my)\s+(?:draft|notice|poll|event)(?:\s+please)?[\s.!]*$/i;

export type CancelKind = "cancel" | "notWhatIMeant";

/** Which way this message cancels, if it does. */
export function cancelKindOf(text: string): CancelKind | null {
  text=text.replace(/\bevnt\b/gi,"event");
  if (/^(?:actually\s+)?i (?:don'?t|do not) want to (?:send|publish|make|create) (?:this|it|that)(?: any\s*more)?[.!?]*$/i.test(text.trim())) return "cancel";
  const parts = text.trim().split(/[,;]\s*/);
  if (parts.length > 1 && parts.every((part) => /^(?:please\s+)?cancel$/i.test(part) || NOT_NOW.test(part))) return "cancel";
  if (/^forget\s+about\s+(?:the|this|my)\s+(?:event|notice|poll)[.!?]*$/i.test(text.trim())) return "cancel";
  if (NOT_WHAT_I_MEANT.test(text)) return "notWhatIMeant";
  if (LOST.test(text)) return "cancel";
  if (CANCEL.test(text)) return "cancel";
  if (NOT_NOW.test(text)) return "cancel";
  if (STOP_IT.test(text)) return "cancel";
  return null;
}
